/**
 * WeatherMap — imperative Leaflet map (bypasses react-leaflet's known
 * StrictMode double-invoke bug that surfaces as "Map container is already
 * initialized").
 *
 * Renders:
 *   - Center marker at the user's locality
 *   - A dashed circle at the search radius
 *   - One marker per community weather report (colored by severity, badged
 *     when verified)
 * Uses OpenStreetMap tiles (no API key needed).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { http } from "@/lib/api";
import { Loader2, Map as MapIcon } from "lucide-react";

const REPORT_ICONS = {
  flooding: "🌊", flash_flood: "🌊", hail: "🌨", heavy_rain: "🌧",
  thunderstorm: "⛈", lightning: "⚡", tornado: "🌪", hurricane: "🌀",
  cyclone: "🌀", high_wind: "💨", wind_damage: "💨", downed_tree: "🌳",
  snow: "🌨", heavy_snow: "🌨", blizzard: "❄", ice: "🧊", ice_storm: "🧊",
  fog: "🌫", dust_storm: "🌪", wildfire: "🔥", smoke: "🔥",
  power_outage: "💡", road_closure: "🚧", landslide: "🏞",
  extreme_heat: "🔥", extreme_cold: "🥶", other: "📍",
};
const SEVERITY_COLORS = {
  mild: "#22c55e", moderate: "#f59e0b", severe: "#f97316", critical: "#dc2626",
};

function reportIcon(type, severity, verified) {
  const emoji = REPORT_ICONS[type] || "📍";
  const color = SEVERITY_COLORS[severity] || "#f59e0b";
  const border = verified ? "#059669" : color;
  return L.divIcon({
    className: "wx-report-marker",
    html: `<div style="width:32px;height:32px;border-radius:50%;background:#fff;
      border:3px solid ${border};display:flex;align-items:center;justify-content:center;
      font-size:16px;box-shadow:0 2px 6px rgba(0,0,0,0.25);">${emoji}</div>`,
    iconSize: [32, 32], iconAnchor: [16, 16], popupAnchor: [0, -14],
  });
}
function centerIcon() {
  return L.divIcon({
    className: "wx-center-marker",
    html: `<div style="width:20px;height:20px;border-radius:50%;background:#0ea5e9;
      border:3px solid #fff;box-shadow:0 0 0 3px rgba(14,165,233,0.35);"></div>`,
    iconSize: [20, 20], iconAnchor: [10, 10],
  });
}

function reportPopupHtml(r) {
  const emoji = REPORT_ICONS[r.type] || "📍";
  const desc = r.description ? `<div style="font-size:12px;margin:4px 0;">${escapeHtml(r.description)}</div>` : "";
  const photo = r.photo_urls?.[0]
    ? `<img src="${r.photo_urls[0]}" alt="" style="margin-top:6px;width:100%;border-radius:4px;" />`
    : "";
  const verified = r.verified ? " ✓ verified" : "";
  return `
    <div style="min-width:200px;">
      <div style="font-weight:600;margin-bottom:4px;">${emoji} ${escapeHtml(r.type.replace(/_/g, " "))}${verified}</div>
      ${desc}
      <div style="font-size:11px;color:#6b7280;">Severity: ${escapeHtml(r.severity)} · ${r.verifications?.length || 0} verifications</div>
      <div style="font-size:10px;color:#6b7280;margin-top:4px;">${new Date(r.created_at).toLocaleString()}</div>
      ${photo}
    </div>
  `;
}
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c]));
}

export default function WeatherMap({ coords, radiusKm = 50 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const radarRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [days, setDays] = useState(3);
  // Layer toggles — every one is opt-in.
  const [showReports, setShowReports] = useState(true);
  const [showRadar,   setShowRadar]   = useState(false);
  const [showTemp,    setShowTemp]    = useState(false);
  const [showClouds,  setShowClouds]  = useState(false);

  // Fetch reports when coords/filters change
  useEffect(() => {
    if (!coords) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          lat: coords.lat, lng: coords.lng,
          radius_km: radiusKm, hours: days * 24, limit: 200,
        });
        if (typeFilter) params.set("type", typeFilter);
        const { data } = await http.get(`/weather-reports?${params.toString()}`);
        if (!cancel) setReports(data.reports || []);
      } catch { /* ignore */ } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [coords, typeFilter, days, radiusKm]);

  // Map lifecycle — init once per component instance.
  useEffect(() => {
    if (!containerRef.current || !coords) return;
    // If Leaflet already owns this DOM node (StrictMode double-invoke), reset it.
    if (containerRef.current._leaflet_id) {
      try { containerRef.current._leaflet_id = undefined; } catch { /* ignore */ }
      containerRef.current.innerHTML = "";
    }
    const map = L.map(containerRef.current, {
      center: [coords.lat, coords.lng],
      zoom: 11,
      scrollWheelZoom: true,
    });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);

    return () => {
      try { map.remove(); } catch { /* ignore */ }
      mapRef.current = null;
      layerRef.current = null;
    };
    // Only re-init if coords change to a truly different locality.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords?.lat, coords?.lng]);

  // Re-render markers when reports or coords change.
  useEffect(() => {
    if (!mapRef.current || !layerRef.current) return;
    layerRef.current.clearLayers();
    // Center marker
    L.marker([coords.lat, coords.lng], { icon: centerIcon() })
      .bindPopup("Your locality")
      .addTo(layerRef.current);
    // Radius circle
    L.circle([coords.lat, coords.lng], {
      radius: radiusKm * 1000,
      color: "#0ea5e9", fillColor: "#0ea5e9", fillOpacity: 0.05,
      weight: 1, dashArray: "4 4",
    }).addTo(layerRef.current);
    // Reports (only when the toggle is on)
    if (showReports) {
      reports.forEach((r) => {
        const m = L.marker([r.lat, r.lng], { icon: reportIcon(r.type, r.severity, r.verified) });
        m.bindPopup(reportPopupHtml(r));
        m.addTo(layerRef.current);
      });
    }
  }, [reports, coords?.lat, coords?.lng, radiusKm, showReports]);

  // Toggleable weather overlay layers.
  // RainViewer (radar) — free, no API key needed.
  // OpenWeatherMap (temp/clouds) — free public sample tiles.
  useEffect(() => {
    if (!mapRef.current) return;
    // Clear whatever overlay was active.
    if (radarRef.current) {
      try { mapRef.current.removeLayer(radarRef.current); } catch { /* noop */ }
      radarRef.current = null;
    }
    if (showRadar) {
      // RainViewer public radar — no key. Timestamp defaults to "now-loop".
      const url = "https://tilecache.rainviewer.com/v2/radar/nowcast_0/512/{z}/{x}/{y}/2/1_1.png";
      radarRef.current = L.tileLayer(url, { opacity: 0.65, maxZoom: 12, attribution: "Rain radar © RainViewer" }).addTo(mapRef.current);
    } else if (showTemp) {
      const url = "https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid=439d4b804bc8187953eb36d2a8c26a02";
      radarRef.current = L.tileLayer(url, { opacity: 0.55, attribution: "Temperature © OpenWeatherMap" }).addTo(mapRef.current);
    } else if (showClouds) {
      const url = "https://tile.openweathermap.org/map/clouds_new/{z}/{x}/{y}.png?appid=439d4b804bc8187953eb36d2a8c26a02";
      radarRef.current = L.tileLayer(url, { opacity: 0.6, attribution: "Clouds © OpenWeatherMap" }).addTo(mapRef.current);
    }
  }, [showRadar, showTemp, showClouds]);

  const distinctTypes = useMemo(
    () => Array.from(new Set(reports.map((r) => r.type))).sort(),
    [reports],
  );

  if (!coords) return null;

  return (
    <div className="mt-12" data-testid="weather-map-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapIcon className="w-4 h-4" />
          <h2 className="font-display text-2xl">Community weather map</h2>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <select
            data-testid="map-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-md border px-2 py-1"
            style={{ borderColor: "var(--wofy-border)" }}
          >
            <option value="">All types</option>
            {distinctTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <select
            data-testid="map-days-filter"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="rounded-md border px-2 py-1"
            style={{ borderColor: "var(--wofy-border)" }}
          >
            <option value={1}>Last 24h</option>
            <option value={3}>Last 3 days</option>
            <option value={7}>Last week</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
      </div>
      <p className="text-xs mb-2" style={{ color: "var(--wofy-text-soft)" }}>
        {loading ? (
          <><Loader2 className="w-3 h-3 inline animate-spin" /> Loading pins…</>
        ) : (
          <>Showing {reports.length} report{reports.length === 1 ? "" : "s"} within {radiusKm} km. Green border = verified by 3+ neighbors.</>
        )}
      </p>

      {/* Layer toggles */}
      <div className="mb-3 flex flex-wrap gap-1.5" data-testid="weather-map-layers">
        <LayerChip label="Community reports" active={showReports} onClick={() => setShowReports((v) => !v)} testid="layer-reports" color="#0ea5e9" />
        <LayerChip label="Live radar" active={showRadar}
                   onClick={() => { setShowRadar((v) => { const nv = !v; if (nv) { setShowTemp(false); setShowClouds(false); } return nv; }); }}
                   testid="layer-radar" color="#3b82f6" />
        <LayerChip label="Temperature" active={showTemp}
                   onClick={() => { setShowTemp((v) => { const nv = !v; if (nv) { setShowRadar(false); setShowClouds(false); } return nv; }); }}
                   testid="layer-temp" color="#ef4444" />
        <LayerChip label="Clouds" active={showClouds}
                   onClick={() => { setShowClouds((v) => { const nv = !v; if (nv) { setShowRadar(false); setShowTemp(false); } return nv; }); }}
                   testid="layer-clouds" color="#6b7280" />
      </div>
      <div
        ref={containerRef}
        data-testid="weather-map"
        className="rounded-2xl overflow-hidden border"
        style={{ borderColor: "var(--wofy-border)", height: 500 }}
      />
    </div>
  );
}

function LayerChip({ label, active, onClick, testid, color }) {
  return (
    <button type="button" onClick={onClick}
            data-testid={testid}
            className={`text-xs px-3 py-1 rounded-full border transition ${active ? "text-white shadow-sm" : "hover:bg-black/5"}`}
            style={{
              borderColor: active ? color : "var(--wofy-border)",
              background:  active ? color : "transparent",
            }}>
      <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
            style={{ background: active ? "#fff" : color, opacity: active ? 0.9 : 0.6 }} />
      {label}
    </button>
  );
}
